import { css } from 'lit'

/**
 * Tonal depth model on the shared liquid-glass material. The material (blur,
 * saturate, hairline border) is one theme definition — `--schmancy-sys-glass-*`
 * — shared with `<schmancy-overlay>`; each tonal name supplies its own base
 * tint (`--glass-base`), so depth is tone, not blur.
 *
 * Translucency is the `opacity` scale (`--glass-opacity`): `translucent` (the
 * default) reads as liquid while keeping contained text legible; `glass` goes
 * more see-through for cards over live content; `solid` goes denser for
 * long-form text and page grounds.
 */
export const surfaceTypeStyles = css`
	/* Tonal types */

	:host([type='surface']) {
		--glass-base: var(--schmancy-sys-color-surface-default);
	}
	:host([type='surfaceDim']) {
		--glass-base: var(--schmancy-sys-color-surface-dim);
	}
	:host([type='surfaceBright']) {
		--glass-base: var(--schmancy-sys-color-surface-bright);
	}
	:host([type='containerLowest']) {
		--glass-base: var(--schmancy-sys-color-surface-containerLowest);
	}
	:host([type='containerLow']) {
		--glass-base: var(--schmancy-sys-color-surface-containerLow);
	}
	:host([type='container']) {
		--glass-base: var(--schmancy-sys-color-surface-container);
	}
	:host([type='containerHigh']) {
		--glass-base: var(--schmancy-sys-color-surface-containerHigh);
	}
	:host([type='containerHighest']) {
		--glass-base: var(--schmancy-sys-color-surface-containerHighest);
	}

	/* One shared material rule for all eight tonal names — fill density is the
	   per-instance --glass-opacity, defaulting to the theme's default fill. */
	:host([type='surface']),
	:host([type='surfaceDim']),
	:host([type='surfaceBright']),
	:host([type='containerLowest']),
	:host([type='containerLow']),
	:host([type='container']),
	:host([type='containerHigh']),
	:host([type='containerHighest']) {
		/* Flat tonal fill — NO backdrop-filter. A single floating surface (dialog,
		   sheet, the show() overlay) earns the blur and stays cheap, but N in-flow
		   surfaces tiled in a scroll list stack N blur layers the compositor must
		   re-blur every paint, which flickers and janks. Glass is an
		   overlay/floating-surface affordance; the base surface stays flat. */
		--glass-opacity: var(--schmancy-sys-glass-fill);
		background: color-mix(in srgb, var(--glass-base) var(--glass-opacity), transparent);
		color: var(--schmancy-sys-color-surface-on);
	}

	/* The glass hairline reads the material's edge against the ground it floats
	   on, so it belongs to the raised container tiers and not to the tiers an
	   app uses as its page ground. Measured across ten real app surfaces:
	   containerLow/container/containerHigh are panes every time they appear,
	   while containerLowest is full-bleed in 10 of 16 appearances — the
	   storefront root, the sign-in ground, the event-page column — and
	   surface/surfaceDim/surfaceBright are M3's ground tiers by definition. On
	   a full-bleed ground the hairline draws a rule around the browser viewport,
	   framing nothing; on a pane it sharpens an edge the tonal fill only
	   suggests.

	   It is an outline for the same reason outlined's edge below is: a border
	   declared here is outranked by the consuming shadow root's own preflight
	   (*, ::before, ::after { border: 0 solid }) from the outer tree, where the
	   cascade sorts encapsulation context before layers and before specificity,
	   which is why this hairline never once painted while it was a border. */
	:host([type='containerLow']),
	:host([type='container']),
	:host([type='containerHigh']),
	:host([type='containerHighest']) {
		outline: 1px solid var(--schmancy-sys-glass-border);
		outline-offset: -1px;
	}

	/* Opacity scale — the "translucent" level resolves to the theme default, so
	   the explicit attribute and the unset default render identically. */
	:host([opacity='glass']) {
		--glass-opacity: 28%;
	}
	:host([opacity='translucent']) {
		--glass-opacity: var(--schmancy-sys-glass-fill);
	}
	:host([opacity='solid']) {
		--glass-opacity: 94%;
	}

	/* Elevated — the one flag that turns blur on. The tonal fill scale above
	   stays flat by default (no backdrop-filter) so a tiled scroll list never
	   stacks N blur layers; elevated opts a single floating/grouping surface
	   (a sticky footer, a card over content) into the same glass material's
	   blur+saturate recipe every other glass surface draws from, so intensity
	   has one source of truth in the theme. */
	:host([elevated]) {
		backdrop-filter: blur(var(--schmancy-sys-glass-blur)) saturate(var(--schmancy-sys-glass-saturate));
		-webkit-backdrop-filter: blur(var(--schmancy-sys-glass-blur)) saturate(var(--schmancy-sys-glass-saturate));
	}

	/* Apple HIG reduced-transparency: thicken the tint to near-opaque and drop
	   the blur, regardless of opacity level, so text stays legible without it. */
	@media (prefers-reduced-transparency: reduce) {
		:host([type='surface']),
		:host([type='surfaceDim']),
		:host([type='surfaceBright']),
		:host([type='containerLowest']),
		:host([type='containerLow']),
		:host([type='container']),
		:host([type='containerHigh']),
		:host([type='containerHighest']) {
			background: color-mix(in srgb, var(--glass-base) 96%, transparent);
			backdrop-filter: none;
			-webkit-backdrop-filter: none;
		}

		:host([elevated]) {
			backdrop-filter: none;
			-webkit-backdrop-filter: none;
		}
	}

	:host([type='transparent']) {
		background-color: transparent;
		color: var(--schmancy-sys-color-surface-on);
	}

	/* The edge is an OUTLINE, not a border, and that is load-bearing rather than
	   stylistic. Every consumer of this component is a shadow root carrying
	   schmancy's baked Tailwind sheet, whose preflight opens with
	   *, ::after, ::before, ::backdrop, ::file-selector-button { border: 0 solid }
	   — a rule that matches this host from the OUTER tree. The shadow-DOM cascade
	   sorts encapsulation context BEFORE layers and before specificity, so for
	   normal declarations the outer tree always wins: a border declared here
	   loses everywhere the component actually ships, and outlined renders
	   indistinguishably from transparent with nothing thrown. outline is the
	   one edge-painting property that universal rule never touches, so this
	   declaration survives; outline-offset: -1px draws it exactly where the
	   border sat, and an outline follows the host's border-radius as a border
	   does. Transition primes for consumer-driven hover/focus effects. */
	:host([type='outlined']) {
		background-color: transparent;
		color: var(--schmancy-sys-color-surface-on);
		outline: 1px solid var(--schmancy-sys-color-outlineVariant);
		outline-offset: -1px;
		transition:
			outline-color 300ms ease,
			box-shadow 300ms ease;
	}

	/* Semantic types — a role's own container pair, at full strength.

	   Each role ships THREE pairs and they are not interchangeable. default/on is
	   the INK pair: it flips tone by scheme so a mark reads against the ambient
	   surface. container/onContainer is the TINTED-SURFACE pair. pinned/onPinned
	   is the scheme-stable SOLID FILL a destructive confirm wears. A tinted
	   surface is the second job, so it paints the second pair — and that pair is
	   additionally the only ground whose ink contrast the theme measures:
	   theme.format.test.ts gates container against onContainer at AA for every
	   role, seed and scheme, and gates nothing about a fraction of it.

	   These rules previously washed 12% of default — the INK token — under
	   onContainer. At 12% over a page ground the ground wins: measured off
	   rendered pixels at a #C19A6B dark theme, error painted rgb(58,45,38) and
	   primary rgb(58,45,28), 1.6 OKLab ΔE apart, and across two seeds and both
	   schemes NO semantic type sat more than 3.7 ΔE from the primary surface
	   beside it. The severity channel schmancy-alert relies on carried nothing at
	   any seed; a warm brand seed only made that legible. lab-semantic-surface
	   re-measures all of it off the rendered swatches.

	   No backdrop-filter here, for the reason the tonal scale states above: N
	   status panels tiled in a list would stack N blur layers. elevated is the
	   one flag that turns blur on, and it reaches these types too. The opacity
	   scale deliberately does not: thinning this fill walks straight back out of
	   the one contrast the theme guarantees. */

	:host([type='primary']) {
		--glass-base: var(--schmancy-sys-color-primary-container);
		--glass-ink: var(--schmancy-sys-color-primary-onContainer);
	}
	:host([type='secondary']) {
		--glass-base: var(--schmancy-sys-color-secondary-container);
		--glass-ink: var(--schmancy-sys-color-secondary-onContainer);
	}
	:host([type='tertiary']) {
		--glass-base: var(--schmancy-sys-color-tertiary-container);
		--glass-ink: var(--schmancy-sys-color-tertiary-onContainer);
	}
	:host([type='error']) {
		--glass-base: var(--schmancy-sys-color-error-container);
		--glass-ink: var(--schmancy-sys-color-error-onContainer);
	}
	:host([type='success']) {
		--glass-base: var(--schmancy-sys-color-success-container);
		--glass-ink: var(--schmancy-sys-color-success-onContainer);
	}
	:host([type='warning']) {
		--glass-base: var(--schmancy-sys-color-warning-container);
		--glass-ink: var(--schmancy-sys-color-warning-onContainer);
	}
	:host([type='info']) {
		--glass-base: var(--schmancy-sys-color-info-container);
		--glass-ink: var(--schmancy-sys-color-info-onContainer);
	}

	/* The glass hairline, for the same reason the raised container tiers carry it
	   and by the same rule: a semantic surface is always a pane, never a page
	   ground. It is also the one channel on this component that is not hue.
	   An M3 container sits close to its own surface in LUMINANCE at every seed —
	   measured across six seeds, container against surface-default is 1.23:1 in
	   light and 1.98:1 in dark for every role, primary included, under the 3:1
	   WCAG 1.4.11 owes a non-text boundary — so hue is what separates the panel
	   from the page, and hue is exactly what a colour-blind reader, or a brand
	   seed landing on the role's own hue, does not have. The hairline is
	   surface-on at 12%, which is a luminance edge in both schemes. */
	:host([type='primary']),
	:host([type='secondary']),
	:host([type='tertiary']),
	:host([type='error']),
	:host([type='success']),
	:host([type='warning']),
	:host([type='info']) {
		background: var(--glass-base);
		color: var(--glass-ink);
		outline: 1px solid var(--schmancy-sys-glass-border);
		outline-offset: -1px;
	}
`

/** Type variants only. The default corner lives on `<schmancy-surface>` itself,
 *  since the other two components that mix this in already own theirs; shadow
 *  and cursor stay stock Tailwind classes applied by the consumer. */
export const surfaceStyles = css`
	${surfaceTypeStyles}
`
